import {
  type CampaignCreationCapabilities,
  type SponsoredBrandAsset,
  type SponsoredDisplayCapabilities,
  type SponsoredDisplayCreationSubmit,
  SponsoredDisplayCreationSubmitSchema,
} from "@adpilot/shared";
import type { TFunction } from "i18next";
import type {
  SponsoredDisplayBidRecommendations,
  SponsoredDisplayProductEligibilityResult,
} from "@/lib/campaign-creation-api";
import { cropFor } from "./creative-assets";
import { currencyLabel, targetBidWithinLimits } from "./money";
import type { SponsoredDisplayDraft } from "./sponsored-display-draft";

export interface SubmitPayloadInput {
  draft: SponsoredDisplayDraft;
  requestId: string;
  capabilities: CampaignCreationCapabilities;
  sponsoredDisplayCapabilities: SponsoredDisplayCapabilities;
  catalogBlocked: boolean;
  eligibilityPending: boolean;
  eligibility: SponsoredDisplayProductEligibilityResult | undefined;
  hasIneligibleProducts: boolean;
  activeBids: SponsoredDisplayBidRecommendations | undefined;
  logoAssets: SponsoredBrandAsset[];
  imageAssets: SponsoredBrandAsset[];
}

export type SubmitPayloadResult =
  | { ok: true; payload: SponsoredDisplayCreationSubmit }
  | { ok: false; message: string };

/**
 * Validates the draft in the order the user should fix it and serializes it into the
 * shared SD submit contract. The first failure wins, so only one message is surfaced.
 */
export function buildSponsoredDisplaySubmitPayload(
  {
    draft,
    requestId,
    capabilities,
    sponsoredDisplayCapabilities,
    catalogBlocked,
    eligibilityPending,
    eligibility,
    hasIneligibleProducts,
    activeBids,
    logoAssets,
    imageAssets,
  }: SubmitPayloadInput,
  t: TFunction<"campaignCreation">,
): SubmitPayloadResult {
  const activeTargets =
    draft.targetingMode === "audience" ? draft.audienceTargets : draft.contextualTargets;
  const suggestedDefaultBid = activeBids?.recommendations.find(
    (recommendation) => recommendation.state === "available",
  );

  if (catalogBlocked || draft.products.length === 0 || eligibilityPending) {
    return { ok: false, message: t("sd.validation.productsRequired") };
  }
  if (eligibility?.capability.state !== "supported") {
    return { ok: false, message: t("sd.validation.eligibilityUnavailable") };
  }
  if (hasIneligibleProducts) {
    return { ok: false, message: t("sd.validation.ineligibleProducts") };
  }
  if (activeTargets.length === 0) {
    return { ok: false, message: t("sd.validation.targetRequired") };
  }
  if (activeTargets.some((row) => !targetBidWithinLimits(row.target.bid, capabilities.bid))) {
    return {
      ok: false,
      message: t("sd.validation.targetBidRange", {
        currency: currencyLabel(capabilities.currencyCode),
        min: capabilities.bid.min,
        max: capabilities.bid.max,
      }),
    };
  }
  const defaultBid =
    draft.bidMode === "custom"
      ? Number(draft.customBid)
      : suggestedDefaultBid?.state === "available"
        ? suggestedDefaultBid.recommended
        : capabilities.bid.min;
  if (
    !Number.isFinite(defaultBid) ||
    defaultBid < capabilities.bid.min ||
    defaultBid > capabilities.bid.max ||
    Number(draft.dailyBudget) < capabilities.dailyBudget.min ||
    Number(draft.dailyBudget) > capabilities.dailyBudget.max
  ) {
    return { ok: false, message: t("sd.validation.invalid") };
  }
  const logoAsset = logoAssets.find((asset) => asset.assetId === draft.creative.logoAssetId);
  const imageAsset = imageAssets.find((asset) => asset.assetId === draft.creative.imageAssetId);
  const customCreative =
    draft.creative.logoEnabled || draft.creative.headlineEnabled || draft.creative.imageEnabled;
  if (
    (!customCreative && sponsoredDisplayCapabilities.creative.dynamic.state !== "supported") ||
    (draft.creative.logoEnabled &&
      sponsoredDisplayCapabilities.creative.logo.state !== "supported") ||
    (draft.creative.headlineEnabled &&
      sponsoredDisplayCapabilities.creative.headline.state !== "supported") ||
    (draft.creative.imageEnabled &&
      sponsoredDisplayCapabilities.creative.image.state !== "supported")
  ) {
    return { ok: false, message: t("sd.validation.creativeUnsupported") };
  }
  if (
    (draft.creative.logoEnabled && !logoAsset) ||
    (draft.creative.imageEnabled && !imageAsset) ||
    (draft.creative.headlineEnabled && !draft.creative.headline.trim())
  ) {
    return { ok: false, message: t("sd.validation.creativeRequired") };
  }
  const candidate = {
    adProduct: "sd" as const,
    requestId,
    campaign: {
      desiredState: "ENABLED" as const,
      name: draft.campaignName,
      portfolioId: draft.portfolioId,
      startDate: draft.startDate,
      endDate: draft.endDate || null,
      dailyBudget: Number(draft.dailyBudget),
      optimizationGoal: draft.optimizationGoal,
    },
    adGroup: { name: draft.adGroupName, defaultBid },
    advertisedProducts: draft.products.map((product) => ({
      asin: product.asin,
      sku: product.sku,
    })),
    targeting:
      draft.targetingMode === "audience"
        ? { type: "audience" as const, targets: draft.audienceTargets.map((row) => row.target) }
        : {
            type: "contextual" as const,
            targets: draft.contextualTargets.map((row) => row.target),
          },
    creative: customCreative
      ? {
          type: "custom" as const,
          headline: draft.creative.headlineEnabled ? draft.creative.headline.trim() || null : null,
          logo:
            draft.creative.logoEnabled && logoAsset
              ? {
                  assetId: logoAsset.assetId,
                  assetVersion: logoAsset.version,
                  crop: cropFor(logoAsset, 6),
                }
              : null,
          image:
            draft.creative.imageEnabled && imageAsset
              ? {
                  assetId: imageAsset.assetId,
                  assetVersion: imageAsset.version,
                  rectCrop: cropFor(imageAsset, 1200 / 628),
                  squareCrop: cropFor(imageAsset, 1),
                }
              : null,
          consentToTranslate: false,
        }
      : { type: "dynamic" as const },
  };
  const result = SponsoredDisplayCreationSubmitSchema.safeParse(candidate);
  if (!result.success) {
    return { ok: false, message: result.error.issues[0]?.message ?? t("sd.validation.invalid") };
  }
  return { ok: true, payload: result.data };
}
